import { getSmplPntColorById } from '@resistapp/client/components/shared/palettes';
import { AbunanceSelection, Filters } from '@resistapp/client/data-utils/filter-data/filter';
import { AbundanceStats } from '@resistapp/common/api-types';
import { GeneGrouping, GetGroup, L2Target, L2Targets, sixteenS } from '@resistapp/common/assays';
import { buildSortIdxByGene } from '@resistapp/common/export-utils';
import { isNonQuantifiedFlag } from '@resistapp/common/flag-utils';
import {
  getFlagForNormalisationMode,
  getNormalisedValue,
  hasNormalisationModeData,
  isAyNormalisationMode,
  isDetectedForNormalisationMode,
} from '@resistapp/common/normalisation-mode';
import { getBioRepGroupKey, getSimpleBioNumber } from '@resistapp/common/sampling-points';
import { getAbundanceLog10Stats } from '@resistapp/common/statistics/abundance-stats';
import {
  Abundance,
  Flags,
  FullAbundance,
  FullProject,
  FullSample,
  FullSamplesByUID,
  NormalisationMode,
  SamplingPoint,
  StrippedFullAbundance,
  XAxisMode,
} from '@resistapp/common/types';
import {
  TRACES_VAL,
  flattenRelevantAbundances,
  flattenSamplesByUID,
  getBioGroupSortValue,
  getDateStrForReplicatedSample,
  getSampleSortValue,
} from '@resistapp/common/utils';
import { sum } from 'd3-array';
import { Dictionary, flatten, groupBy, keyBy, keys, mapValues, sortBy, uniq, uniqBy, values } from 'lodash';
import { ExperimentalMetrics } from './build-experimental-metrics';

export type BarOrBoxPlotData = BoxPlotData | BarPlotData;

export type SingleBoxPlotSeries = BoxDatum[];
export type SingleBarPlotSeries = BarDatum[];

export type BoxPlotData = SingleBoxPlotSeries[];
export type BarPlotData = SingleBarPlotSeries[];

export interface DateDatum {
  date: string;
  label: string;
}

export interface PlotDatum extends DateDatum {
  color: string;
  sampleId: string;
  sampleNumber: string;
  bioNumber: string;
  samplingPointId: number;
}

export interface BoxDatum extends PlotDatum, AbundanceStats {
  experimentalMetrics?: ExperimentalMetrics;
  traceOnly?: boolean;
}

export interface BarDatum extends PlotDatum {
  measuredByGene?: Record<string, boolean>;
  flagByGene?: Record<string, Flags | null>;
  [key: string]: string | number | Record<string, boolean> | Record<string, Flags | null> | undefined;
}

export interface ResearchPlotData {
  againstTime: boolean; // legacy, to be deprecated, use xAxisMode instead
  grouping: GeneGrouping;
  geneGroups: string[];
  numAnalyzed: number;
  normalisationMode: NormalisationMode;
  boxData?: BoxPlotData; // array of box series (ie. groups)
  barData?: BarPlotData; // array of bar series (ie. groups) of dicts keyd by date and targets / genes
  heatData?: BarPlotData; // array of bar series (grouped, coinciding and bio samples smashed together) of dicts keyd by date and targets / genes
  sortIdxByGene: Dictionary<number>;
}

interface TechRepBoxConfig {
  assay: string;
  normalisationMode: NormalisationMode;
}

function hasModeResult(abundance: FullAbundance, mode: NormalisationMode): boolean {
  return hasNormalisationModeData(abundance, mode) || !isAyNormalisationMode(mode);
}

function filterModeCompatibleAbundances(
  abundances: FullAbundance[] | Abundance[],
  mode: NormalisationMode,
): FullAbundance[] {
  return (abundances as FullAbundance[]).filter(abundance => hasModeResult(abundance, mode));
}

function compactSortIdxByVisibleGenes(sortIdxByGene: Dictionary<number>, visibleGenes: string[]): Dictionary<number> {
  return sortBy(uniq(visibleGenes), gene => sortIdxByGene[gene] ?? Number.MAX_SAFE_INTEGER).reduce<Dictionary<number>>(
    (acc, gene, idx) => {
      acc[gene] = idx;
      return acc;
    },
    {},
  );
}

export type PartialFilters = Pick<
  Filters,
  | 'selectedTargets'
  | 'selectedTargetGrouping'
  | 'normalisationMode'
  | 'abundances'
  | 'selectedSamplingPointIdsOrdered'
  | 'xAxisMode'
>;

export function buildResearchPlotData(
  project: FullProject,
  filteringOptions: PartialFilters,
  allGeneGroups: Record<string, Array<L2Targets | string>>,
  getGroup: GetGroup,
): ResearchPlotData | null {
  // Note: filtering is already applied
  const maybeFocusedByBioGroupKey = project.focusedByUID || project.samplesByUID;
  const {
    selectedTargets,
    selectedTargetGrouping,
    normalisationMode,
    abundances: abundanceSelection,
    selectedSamplingPointIdsOrdered,
    xAxisMode,
  } = filteringOptions;

  // Assume all samples have time and pick one time for replicated samples
  const dateByBioGroupKey = mapValues(maybeFocusedByBioGroupKey, getDateStrForReplicatedSample);

  const envColorById = getSmplPntColorById(project);
  const allSamples = flattenSamplesByUID(project.samplesByUID);
  if (!allSamples.length) {
    // Legacy projects
    return null;
  }
  const allRelAbundances = flattenRelevantAbundances(allSamples, selectedTargetGrouping === sixteenS);
  const relevantRelAbundances = filterModeCompatibleAbundances(allRelAbundances, normalisationMode).filter(datum =>
    getGroup(datum.assay, selectedTargetGrouping),
  );
  const unsortedGeneGroups = uniq(relevantRelAbundances.map(datum => getGroup(datum.assay, selectedTargetGrouping)));
  const geneGroups = allGeneGroups[selectedTargetGrouping]
    .filter(g => unsortedGeneGroups.includes(g as L2Target))
    .reverse();
  const explicitlyEnabledGeneGroups = geneGroups.filter(t => selectedTargets.includes(t as L2Target));
  const enabledGeneGroups = explicitlyEnabledGeneGroups.length > 0 ? explicitlyEnabledGeneGroups : geneGroups;
  const enabledGeneGroupSet = new Set<string | undefined>(enabledGeneGroups);
  const numAnalyzed = uniqBy(relevantRelAbundances, datum => datum.assay).filter(datum =>
    enabledGeneGroupSet.has(getGroup(datum.assay, selectedTargetGrouping)),
  ).length;

  const maybeFocusedSamples = flattenSamplesByUID(maybeFocusedByBioGroupKey);
  const baseSortIdxByGene = buildSortIdxByGene(allSamples, selectedTargetGrouping, getGroup, maybeFocusedSamples);
  const visibleGenes = relevantRelAbundances
    .filter(datum => enabledGeneGroupSet.has(getGroup(datum.assay, selectedTargetGrouping)))
    .map(datum => datum.gene);
  const sortIdxByGene =
    normalisationMode === NormalisationMode.LITRE_AY || normalisationMode === NormalisationMode.TEN_UL_DILUTED_DNA_AY
      ? compactSortIdxByVisibleGenes(baseSortIdxByGene, visibleGenes)
      : baseSortIdxByGene;
  const showTraces = abundanceSelection !== AbunanceSelection.QUANTIFIED_ONLY;
  const singleAssaySelection =
    selectedTargetGrouping === 'assay' && selectedTargets.length === 1 ? String(selectedTargets[0]) : null;
  const techRepBoxConfig: TechRepBoxConfig | undefined = singleAssaySelection
    ? { assay: singleAssaySelection, normalisationMode }
    : undefined;
  switch (xAxisMode) {
    case XAxisMode.TIME: {
      if (!values(dateByBioGroupKey).every(Boolean)) {
        // Samples without time not supported in TIME mode
        return null;
      }
      // Plot against time iff some sampling point is sampled (non-replicatedly) on different times.
      // Plotting code assumes that box groups are from the same time point.

      const groupedByTime = groupBy(maybeFocusedSamples, sample => dateByBioGroupKey[getBioRepGroupKey(sample)]);
      const data = values(groupedByTime).map(timeSamples => {
        const { boxData, barData, heatData } = getBarAndBoxDataForCoincidingSamplesBySmplPnt(
          timeSamples,
          normalisationMode,
          dateByBioGroupKey as Dictionary<string>,
          selectedTargetGrouping,
          geneGroups,
          techRepBoxConfig,
          showTraces,
          abundanceSelection === AbunanceSelection.ESTIMATES,
          abundanceSelection === AbunanceSelection.MEASURED_OR_ESTIMATE,
          envColorById,
          selectedSamplingPointIdsOrdered,
          project.id,
          getGroup,
        );
        return {
          boxData,
          barData,
          heatData: flatten(heatData),
        };
      });
      return {
        againstTime: true,
        geneGroups,
        numAnalyzed,
        grouping: selectedTargetGrouping,
        normalisationMode,
        boxData: data.map(d => d.boxData),
        barData: data.map(d => d.barData),
        heatData: data.map(d => d.heatData),
        sortIdxByGene,
      };
    }
    case XAxisMode.ORDINAL_GROUPED: {
      // Build one plot group per BioRep UID (sampling point + time).

      // Index the selection order and the sort values once instead of scanning them for every comparison
      const selectionIndexBySamplingPointId = new Map(selectedSamplingPointIdsOrdered.map((id, index) => [id, index]));
      const getSelectionIndex = (samplingPointId: number) => selectionIndexBySamplingPointId.get(samplingPointId) ?? -1;
      const bioGroupSortValueByKey = mapValues(maybeFocusedByBioGroupKey, groupSamples =>
        getBioGroupSortValue(groupSamples, project.id),
      );
      const orderedBioGroupKeys = keys(maybeFocusedByBioGroupKey).sort((uidA, uidB) => {
        const samplesA = maybeFocusedByBioGroupKey[uidA];
        const samplesB = maybeFocusedByBioGroupKey[uidB];

        const envA = samplesA[0].samplingPoint.id;
        const envB = samplesB[0].samplingPoint.id;

        // 1. Sampling point order - honour query parameter order if given
        if (selectedSamplingPointIdsOrdered.length) {
          const idxA = getSelectionIndex(envA);
          const idxB = getSelectionIndex(envB);
          if (idxA !== idxB) return idxA - idxB;
        } else if (envA !== envB) {
          return envA - envB;
        }

        // 2. Project insertion order within sampling point bio replicas
        return bioGroupSortValueByKey[uidA] - bioGroupSortValueByKey[uidB];
        // NOTE: this might not be that meaningful if we start copying and moving samples.
        // An alternative would be to use sampling date:
        // const timeA = dateByBioGroupKey[uidA] || '';
        // const timeB = dateByBioGroupKey[uidB] || '';
        // if (timeA !== timeB) return timeA.localeCompare(timeB);
      });

      const boxData: BoxPlotData = [];
      const barData: BarPlotData = [];
      const heatData: BarPlotData = [];

      orderedBioGroupKeys.forEach(bioGroupKey => {
        const groupSamples = maybeFocusedByBioGroupKey[bioGroupKey];
        const samplingPoint = groupSamples[0].samplingPoint;
        const time = dateByBioGroupKey[bioGroupKey] || '';
        const focusedAbundances = filterModeCompatibleAbundances(
          flattenRelevantAbundances(groupSamples, selectedTargetGrouping === sixteenS),
          normalisationMode,
        );

        // Box (one datum) inside series
        const boxDatum = buildBoxDatum(
          samplingPoint,
          focusedAbundances,
          normalisationMode,
          envColorById,
          time,
          String(groupSamples[0].number),
          project.id,
          groupSamples[0],
          techRepBoxConfig,
          getGroup,
        );
        boxData.push(boxDatum ? [boxDatum] : []);

        // Detected genes bar (one stacked bar) inside series
        barData.push([
          buildBarDatum(
            samplingPoint,
            selectedTargetGrouping,
            geneGroups,
            focusedAbundances,
            normalisationMode,
            time,
            envColorById,
            String(groupSamples[0].number),
            project.id,
            groupSamples[0],
            getGroup,
          ),
        ]);

        // Heat series (one column per bio rep sample)
        heatData.push(
          buildSingleHeatMapSeries(
            groupSamples,
            selectedTargetGrouping,
            normalisationMode,
            showTraces,
            abundanceSelection === AbunanceSelection.ESTIMATES,
            abundanceSelection === AbunanceSelection.MEASURED_OR_ESTIMATE,
            time,
            envColorById,
            project.id,
          ),
        );
      });

      return {
        againstTime: false,
        geneGroups,
        numAnalyzed,
        grouping: selectedTargetGrouping,
        normalisationMode,
        boxData,
        barData,
        heatData,
        sortIdxByGene,
      };
    }
    case XAxisMode.ORDINAL_INDIVIDUAL: {
      // Preserve the exact project insertion order for the INDIVIDUAL x-axis mode.
      // Using only the project sample id (via getSampleSortValue) avoids any additional
      // grouping logic – such as sampling point or bio-rep – that would otherwise
      // reorder the data. This guarantees 1A, 2A, … 8A, 1B, 2B … just as they were
      // added to the project.

      // NOTE: This does not respect selection order!
      const orderedSamples = sortBy(maybeFocusedSamples, s => getSampleSortValue(s, project.id));

      const boxData: BoxPlotData = [];
      const barData: BarPlotData = [];
      const heatData: BarPlotData = [];

      orderedSamples.forEach(sample => {
        const abundances = filterModeCompatibleAbundances(
          flattenRelevantAbundances([sample], selectedTargetGrouping === sixteenS),
          normalisationMode,
        );
        const time = sample.time ? sample.time.toISOString() : '';
        const boxDatum = buildBoxDatum(
          sample.samplingPoint,
          abundances,
          normalisationMode,
          envColorById,
          time,
          String(sample.number),
          project.id,
          sample,
          techRepBoxConfig,
          getGroup,
        );
        boxData.push(boxDatum ? [boxDatum] : []);
        barData.push([
          buildBarDatum(
            sample.samplingPoint,
            selectedTargetGrouping,
            geneGroups,
            abundances,
            normalisationMode,
            time,
            envColorById,
            String(sample.number),
            project.id,
            sample,
            getGroup,
          ),
        ]);
        heatData.push(
          buildSingleHeatMapSeries(
            [sample],
            selectedTargetGrouping,
            normalisationMode,
            showTraces,
            abundanceSelection === AbunanceSelection.ESTIMATES,
            abundanceSelection === AbunanceSelection.MEASURED_OR_ESTIMATE,
            time,
            envColorById,
            project.id,
          ),
        );
      });
      return {
        againstTime: false,
        geneGroups,
        numAnalyzed,
        grouping: selectedTargetGrouping,
        normalisationMode,
        boxData,
        barData,
        heatData,
        sortIdxByGene,
      };
    }
    default:
      return null;
  }
}

export function getBarDatumKeys(datum: BarDatum) {
  return keys(datum).filter(
    key =>
      key !== 'date' &&
      key !== 'label' &&
      key !== 'color' &&
      key !== 'sampleNumber' &&
      key !== 'sampleId' &&
      key !== 'samplingPointId' &&
      key !== 'sortIndex' &&
      key !== 'bioNumber' &&
      key !== 'measuredByGene' &&
      key !== 'flagByGene',
  );
}

export function getNumDetectedTotal(datum: BarDatum) {
  return sum(getBarDatumKeys(datum).map(key => +(datum[key] ?? 0)));
}

export function isExtraWideProject(samplesByUID: FullSamplesByUID) {
  const numSamples = keys(samplesByUID).length;
  const numSamplesTreshold = 70; // 70th sample triggers fourth sample leegend column
  return numSamples >= numSamplesTreshold;
}

function getBarAndBoxDataForCoincidingSamplesBySmplPnt(
  maybeFocusedSamples: FullSample[],
  normalisationMode: NormalisationMode,
  dateByUID: Dictionary<string>,
  grouping: GeneGrouping,
  geneGroups: string[],
  techRepBoxConfig: TechRepBoxConfig | undefined,
  showTraces: boolean,
  useEstimate: boolean,
  fallbackToEstimate: boolean,
  envColorById: Dictionary<string>,
  envIdOrder: number[],
  projectId: number,
  getGroup: GetGroup,
) {
  // Combine sample numbers if many exist for the same smplPnt and time.
  const groupedBySmplPnt = groupBy(maybeFocusedSamples, sample => sample.samplingPoint.id);
  const envIds = keys(groupedBySmplPnt);
  const sortedSmplPntIds = sortBy(envIds, id => envIdOrder.indexOf(parseInt(id, 10)));
  return sortedSmplPntIds.reduce<{ boxData: BoxDatum[]; barData: BarDatum[]; heatData: BarDatum[][] }>(
    (soFar, envId) => {
      const envSamples = groupedBySmplPnt[envId];
      const sample = envSamples[0];
      const samplingPoint = sample.samplingPoint;
      const focusedAbundances = filterModeCompatibleAbundances(
        flattenRelevantAbundances(envSamples, grouping === sixteenS), // sixteenS check is needed in case samples aren't focused yet
        normalisationMode,
      );
      const sampleUIDs = uniq(envSamples.map(sampleLocal => getBioRepGroupKey(sampleLocal)));

      const firstBioRepKey = sampleUIDs[0];
      const time = dateByUID[firstBioRepKey] && new Date(dateByUID[firstBioRepKey]).toISOString();

      const boxDatum = buildBoxDatum(
        samplingPoint,
        focusedAbundances,
        normalisationMode,
        envColorById,
        time,
        String(sample.number),
        projectId,
        sample,
        techRepBoxConfig,
        getGroup,
      );

      return {
        boxData: boxDatum ? [...soFar.boxData, boxDatum] : soFar.boxData,
        barData: [
          ...soFar.barData,
          buildBarDatum(
            samplingPoint,
            grouping,
            geneGroups,
            focusedAbundances,
            normalisationMode,
            time,
            envColorById,
            String(sample.number),
            projectId,
            sample,
            getGroup,
          ),
        ],
        heatData: [
          ...soFar.heatData,
          [
            ...buildSingleHeatMapSeries(
              envSamples,
              grouping,
              normalisationMode,
              showTraces,
              useEstimate,
              fallbackToEstimate,
              time,
              envColorById,
              projectId,
            ),
          ],
        ],
      };
    },
    { barData: [], boxData: [], heatData: [] },
  );
}

function buildSingleHeatMapSeries(
  samples: FullSample[],
  grouping: GeneGrouping,
  normalisationMode: NormalisationMode,
  showTraces: boolean,
  useEstimate: boolean,
  fallbackToEstimate: boolean,
  time: string,
  envColorById: Dictionary<string>,
  _projectId: number,
): SingleBarPlotSeries {
  const samplingPoint = samples[0].samplingPoint;
  const sortedSamples = sortBy(samples, sample => getSimpleBioNumber(sample));
  const result = sortedSamples.map(sample => {
    const abundances = filterModeCompatibleAbundances(
      flattenRelevantAbundances([sample], grouping === sixteenS),
      normalisationMode,
    );
    const abundanceByGene = keyBy(abundances, datum => datum.gene);
    const valueByGene = mapValues(abundanceByGene, datum => {
      let baseValue: number | null;
      if (useEstimate || (fallbackToEstimate && !datum.measured)) {
        baseValue = datum.estimates?.[normalisationMode] ?? null;
      } else {
        baseValue = getNormalisedValue(datum, normalisationMode);
      }
      return showTraces && isNonQuantifiedFlag(getFlagForNormalisationMode(datum, normalisationMode))
        ? TRACES_VAL
        : baseValue;
    });
    const measuredByGene = mapValues(abundanceByGene, datum => datum.measured);
    const modeFlagByGene = mapValues(abundanceByGene, datum =>
      showTraces ? getFlagForNormalisationMode(datum, normalisationMode) : null,
    );
    const hasAnyModeFlag = values(modeFlagByGene).some(flag => flag != null);
    const flagByGene = hasAnyModeFlag || grouping === sixteenS ? modeFlagByGene : {};
    return {
      date: time,
      label: samplingPoint.name,
      color: envColorById[samplingPoint.id],
      samplingPointId: samplingPoint.id,
      bioNumber: getSimpleBioNumber(sample),
      sampleNumber: String(sample.number),
      sampleId: String(sample.id),
      ...valueByGene,
      measuredByGene,
      flagByGene,
    } satisfies BarDatum;
  });
  return result;
}

function buildBarDatum(
  samplingPoint: SamplingPoint,
  grouping: GeneGrouping,
  geneGroups: string[],
  abundances: FullAbundance[],
  normalisationMode: NormalisationMode,
  time: string,
  envColorById: Dictionary<string>,
  sampleNumber: string,
  _projectId: number,
  sample: FullSample,
  getGroup: GetGroup,
): BarDatum {
  // Count the distinct detected genes of each group in one pass over the abundances
  const detectedGenesByGroup = new Map<string | undefined, Set<string>>();
  abundances.forEach(datum => {
    if (!isDetectedForNormalisationMode(datum, normalisationMode)) {
      return;
    }
    const group = getGroup(datum.assay, grouping);
    const genes = detectedGenesByGroup.get(group) ?? new Set<string>();
    genes.add(datum.gene);
    detectedGenesByGroup.set(group, genes);
  });
  return {
    date: time,
    label: samplingPoint.name,
    color: envColorById[samplingPoint.id],
    samplingPointId: samplingPoint.id,
    ...geneGroups.reduce<Dictionary<string>>((acc, geneGroup) => {
      const count = detectedGenesByGroup.get(geneGroup)?.size ?? 0;
      acc[geneGroup] = `${count}`;
      return acc;
    }, {}),
    bioNumber: getSimpleBioNumber(sample),
    sampleNumber,
    sampleId: String(sample.id),
  } satisfies BarDatum;
}

function getTechRepAwareBoxStats(
  focusedAbundances: FullAbundance[],
  techRepBoxConfig: TechRepBoxConfig | undefined,
  fallbackMode: NormalisationMode,
  getGroup: GetGroup,
): AbundanceStats {
  const baseStats = () =>
    getAbundanceLog10Stats(
      focusedAbundances,
      {
        scope: 'detected',
        mode: fallbackMode,
        targets: values(L2Targets),
      },
      getGroup,
    );

  if (!techRepBoxConfig) {
    return baseStats();
  }

  const { assay, normalisationMode } = techRepBoxConfig;
  const numericReplicates = getNumericTechRepValues(
    focusedAbundances.filter(abundance => abundance.assay === assay),
    normalisationMode,
  );

  if (!numericReplicates.length) {
    return baseStats();
  }

  const replicateAbundances: StrippedFullAbundance[] = numericReplicates.map(value => ({
    assay,
    gene: assay,
    relative: normalisationMode === NormalisationMode.SIXTEEN_S ? value : null,
    absolute: normalisationMode === NormalisationMode.TEN_UL_DILUTED_DNA_16S ? value : null,
    copiesPerL: normalisationMode === NormalisationMode.LITRE_16S ? value : null,
    copiesPerHour: normalisationMode === NormalisationMode.HOUR ? value : null,
    copiesPerMgSS: normalisationMode === NormalisationMode.MG_SS ? value : null,
    copiesPerMgBod: normalisationMode === NormalisationMode.MG_BOD ? value : null,
    valuesByMode: { [normalisationMode]: value },
  }));

  return getAbundanceLog10Stats(
    replicateAbundances,
    {
      scope: 'detected',
      mode: normalisationMode,
      targets: values(L2Targets),
    },
    getGroup,
  );
}

function getNumericTechRepValues(abundances: FullAbundance[], normalisationMode: NormalisationMode): number[] {
  return abundances
    .flatMap(abundance => abundance.techRepValuesByMode?.[normalisationMode] ?? [])
    .filter((value): value is number => value != null);
}

function buildBoxDatum(
  samplingPoint: SamplingPoint,
  focusedAbundances: FullAbundance[],
  normalisationMode: NormalisationMode,
  envColorById: Dictionary<string>,
  time: string,
  sampleNumber: string,
  _projectId: number,
  sample: FullSample,
  techRepBoxConfig: TechRepBoxConfig | undefined,
  getGroup: GetGroup,
): BoxDatum | null {
  if (techRepBoxConfig) {
    const selectedAssayAbundances = focusedAbundances.filter(abundance => abundance.assay === techRepBoxConfig.assay);
    const hasQuantifiedSelectedAssay = selectedAssayAbundances.some(
      abundance =>
        abundance.meanCt != null && getNormalisedValue(abundance, techRepBoxConfig.normalisationMode) != null,
    );

    if (!hasQuantifiedSelectedAssay) {
      return null;
    }
  } else if (!focusedAbundances.some(abundance => getNormalisedValue(abundance, normalisationMode) != null)) {
    return null;
  }

  const data = getTechRepAwareBoxStats(focusedAbundances, techRepBoxConfig, normalisationMode, getGroup);

  return {
    ...data,
    date: time,
    label: samplingPoint.name,
    color: envColorById[samplingPoint.id],
    samplingPointId: samplingPoint.id,
    experimentalMetrics: undefined, // buildExperimentalMetrix(focusedAbundances),
    bioNumber: getSimpleBioNumber(sample),
    sampleNumber,
    sampleId: String(sample.id),
  };
}
